import LoginIcon from '@mui/icons-material/Login';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';
import { StyledButton } from '../ui';

/**
 * @typedef {Object} ActionButton
 * @property {string} text - Button label
 * @property {string} link - Link URL (internal route or external URL; external opens in a new tab)
 * @property {'person'|'login'|'open'} [icon] - Optional leading icon
 */

/**
 * @typedef {Object} ServiceCardProps
 * @property {string} category - Service category (e.g., "SAAS", "PROFESSIONAL SERVICES")
 * @property {string} title - Service title
 * @property {string} description - Service description
 * @property {string} buttonText - Button text label
 * @property {string} link - Link URL (internal route, external URL, or hash anchor)
 * @property {number} [index] - Card index for alternating corner pattern
 * @property {string} [buttonVariant] - Button variant ('contained', 'outlined')
 * @property {boolean} [fullWidth] - Whether button should be full width
 * @property {string} [badgeUrl] - Optional badge image URL to display between description and button (right-aligned)
 * @property {string} [badgeAlt] - Alt text for badge image
 * @property {boolean} [comingSoon] - If true, button will be disabled with "Coming Soon" styling
 * @property {ActionButton} [actionButton] - Optional secondary action rendered as a small outlined button in the tile's top-right corner (e.g. EllaFin "Sign In")
 */

/** Icons an action button may request by name (kept small on purpose). */
const ACTION_ICONS = {
  person: PersonIcon,
  login: LoginIcon,
  open: OpenInNewIcon,
};

/** Form pages are hard-reloaded to avoid reCAPTCHA conflicts with client routing. */
const FORM_PAGES = [
  '/early-access-program',
  '/discuss-project',
  '/lets-collaborate',
  '/assessment/',
  '/ebooks/',
  '/solution-briefs/',
  '/business-outcome/',
  '/case-studies/rising-routes-alliance',
  '/case-studies/nonprofit-amazon-connect',
];

const isExternal = (link) =>
  typeof link === 'string' &&
  (link.startsWith('http://') || link.startsWith('https://'));

/**
 * ServiceCard - Molecule component for marketplace service offerings
 *
 * Displays a service card with category, title, description, and action button.
 * Features alternating corner border patterns for visual interest.
 * Maintains exact styling from MarketplaceSection2 for zero visual regression.
 *
 * Design Features:
 * - White background with rounded corners
 * - Alternating green corner borders (even: top-left/bottom-right, odd: top-right/bottom-left)
 * - Hover effect with lift and shadow
 * - Typography hierarchy: category > title > description
 * - Action button at bottom
 * - Optional small "corner action" button (top-right), e.g. Sign In
 *
 * @param {ServiceCardProps} props - Component props
 * @returns {React.ReactElement} Service card component
 */
function ServiceCardComponent({
  category,
  title,
  description,
  buttonText,
  link,
  index = 0,
  buttonVariant = 'contained',
  fullWidth = false,
  badgeUrl,
  badgeAlt,
  comingSoon = false,
  actionButton,
}) {
  const navigate = useNavigate();
  const { trackCTA } = useAnalytics();

  const isEven = index % 2 === 0;
  const hasAction = Boolean(actionButton?.link && actionButton?.text);
  const ActionIcon = hasAction ? ACTION_ICONS[actionButton.icon] : null;

  /**
   * Follow a tile link: external → new tab, hash → scroll, internal → route.
   * @param {string} target - Link to follow
   */
  const openLink = (target) => {
    if (!target || target === '#') {
      return;
    }

    if (isExternal(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else if (target.startsWith('#')) {
      const element = document.querySelector(target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (FORM_PAGES.some((path) => target.includes(path))) {
      window.location.href = target;
    } else {
      navigate(target);
    }
  };

  const handleClick = () => {
    // Don't do anything if coming soon
    if (comingSoon) {
      return;
    }

    // Track the CTA click with service title and category
    trackCTA(buttonText, `ServiceCard_${category}_${title}`);
    openLink(link);
  };

  const handleActionClick = (event) => {
    trackCTA(actionButton.text, `ServiceCard_Action_${category}_${title}`, {
      destination: actionButton.link,
    });
    // External links are real anchors (href/target); internal ones route.
    if (!isExternal(actionButton.link)) {
      event.preventDefault();
      openLink(actionButton.link);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'var(--color-card)',
        position: 'relative',
        p: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '280px',
        overflow: 'visible',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -1,
          left: isEven ? -1 : 'auto',
          right: !isEven ? -1 : 'auto',
          width: '50px',
          height: '50px',
          borderTop: '3px solid var(--color-primary)',
          borderLeft: isEven ? '3px solid var(--color-primary)' : 'none',
          borderRight: !isEven ? '3px solid var(--color-primary)' : 'none',
          borderRadius: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -1,
          right: isEven ? -1 : 'auto',
          left: !isEven ? -1 : 'auto',
          width: '50px',
          height: '50px',
          borderBottom: '3px solid var(--color-primary)',
          borderRight: isEven ? '3px solid var(--color-primary)' : 'none',
          borderLeft: !isEven ? '3px solid var(--color-primary)' : 'none',
          borderRadius: 0,
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          willChange: 'transform, box-shadow',
        },
      }}
    >
      {hasAction && (
        <StyledButton
          variant="outlined"
          size="small"
          color="var(--color-heading)"
          href={actionButton.link}
          {...(isExternal(actionButton.link) && {
            target: '_blank',
            rel: 'noopener noreferrer',
          })}
          onClick={handleActionClick}
          startIcon={
            ActionIcon
              ? <ActionIcon sx={{ fontSize: '1rem !important' }} />
              : undefined
          }
          aria-label={`${actionButton.text} – ${title}`}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            zIndex: 2,
            minWidth: 'auto',
            py: 0.5,
            px: 1.5,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            borderWidth: '1.5px',
            borderTopRightRadius: '10px',
            borderBottomLeftRadius: '10px',
            '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
            '&:hover': {
              backgroundColor: 'var(--color-background-light)',
              borderColor: 'var(--color-primary)',
              color: 'var(--color-primary)',
              borderWidth: '1.5px',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {actionButton.text}
        </StyledButton>
      )}

      <Typography
        variant="caption"
        sx={{
          color: 'var(--color-text-muted)',
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          display: 'block',
          mb: 1,
          pr: hasAction ? '112px' : 0,
        }}
      >
        {category}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          color: 'var(--color-primary)',
          fontWeight: 600,
          fontSize: '1.25rem',
          mb: 1,
          pr: hasAction ? '112px' : 0,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'var(--color-text-muted)',
          fontSize: '0.9rem',
          lineHeight: 1.5,
          flex: 1,
          mb: 2,
        }}
      >
        {description}
      </Typography>

      {badgeUrl && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: -10,
            mb: 2,
          }}
        >
          <Box
            component="img"
            src={badgeUrl}
            alt={badgeAlt || 'Badge'}
            sx={{
              width: { xs: '60px', sm: '60px', md: '68px' },
              height: 'auto',
            }}
          />
        </Box>
      )}

      <StyledButton
        variant={buttonVariant}
        size="small"
        onClick={handleClick}
        fullWidth={fullWidth}
        disabled={comingSoon}
        sx={{
          alignSelf: fullWidth ? 'stretch' : 'flex-start',
          mt: 'auto',
          fontSize: '0.875rem',
          py: 1.25,
          px: 2.5,
          width: fullWidth ? '100%' : 'auto',
          ...(comingSoon && {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: 'var(--color-muted)',
            color: 'var(--color-text-muted)',
            '&:hover': {
              backgroundColor: 'var(--color-muted)',
              transform: 'none',
              boxShadow: 'none',
            },
          }),
        }}
      >
        {comingSoon ? 'Coming Soon' : buttonText}
      </StyledButton>
    </Box>
  );
}

ServiceCardComponent.propTypes = {
  category: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  index: PropTypes.number,
  buttonVariant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  fullWidth: PropTypes.bool,
  badgeUrl: PropTypes.string,
  badgeAlt: PropTypes.string,
  comingSoon: PropTypes.bool,
  actionButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    icon: PropTypes.oneOf(['person', 'login', 'open']),
  }),
};

const ServiceCard = memo(ServiceCardComponent);
ServiceCard.displayName = 'ServiceCard';

export default ServiceCard;
